import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2, AlertTriangle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Super Admin-only email diagnostics card.
 *
 * Mirrors the no-`?to=` snapshot path of `/api/admin/email/health` on mount
 * to surface a green/red status pill. The "Send test-e-post" button hits
 * the same endpoint with `?to=<email>` and shows the result inline.
 *
 * Renders nothing for non-platform-admin users. The backend gates the
 * endpoint independently — this is a UX-level shortcut, not a security
 * boundary.
 */
export default function EmailHealthCard() {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [lastResult, setLastResult] = useState(null); // { ok, message }
  // `verified` reflects the live test-send outcome in this browser session
  // and overrides the config-only OK/Feil pill once we have an actual
  // delivery result. null = "not tested yet, fall back to config check".
  const [verified, setVerified] = useState(null); // null | "ok" | "fail"

  // Prefill the test-send input with the logged-in admin email once
  // we have the user object.
  useEffect(() => {
    if (user?.email && !testEmail) setTestEmail(user.email);
  }, [user, testEmail]);

  const loadSnapshot = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email/health");
      setSnapshot(r.data);
    } catch {
      // Non-admin (or auth issue) — silently hide the card.
      setSnapshot(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSnapshot(); }, []);

  // Hide for anyone the backend doesn't classify as platform admin.
  if (loading) return null;
  if (snapshot === false) return null;

  // Pill colour priority:
  //  1. session test-send result (verified === "ok" | "fail")
  //  2. otherwise the config snapshot (provider + key set)
  // The session result wins so a stale-but-set RESEND_API_KEY no longer
  // shows a misleading green pill after a failed test.
  const configOk =
    snapshot?.provider === "resend" && snapshot?.resend_api_key_set === true;
  const pillOk = verified === "ok" ? true : verified === "fail" ? false : configOk;

  const submitTest = async (e) => {
    e?.preventDefault();
    const to = (testEmail || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Skriv inn en gyldig e-postadresse");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const r = await api.get(`/admin/email/health`, { params: { to } });
      const ts = r.data?.test_send;
      // Resend success → status === "delivered"; mock → "delivered_mock";
      // anything else (including null) is treated as a failure for UX
      // purposes since it means the real recipient won't actually receive
      // an email.
      const ok = ts?.status === "delivered";
      if (ok) {
        const id = ts.provider_id ? ` (id: ${ts.provider_id})` : "";
        setLastResult({ ok: true, message: `Test-e-post sendt til ${ts.to}${id}` });
        setVerified("ok");
        toast.success("Test-e-post sendt");
      } else if (ts?.status === "delivered_mock") {
        setLastResult({
          ok: false,
          message:
            "Mock-provider er aktiv — ingen ekte e-post ble sendt. Sjekk EMAIL_PROVIDER og RESEND_API_KEY i miljøet.",
        });
        setVerified("fail");
        toast.error("Mock-provider aktiv");
      } else {
        const err = ts?.error || "ukjent feil";
        setLastResult({ ok: false, message: `Kunne ikke sende test-e-post: ${err}` });
        setVerified("fail");
        toast.error("Kunne ikke sende test-e-post");
      }
      // Refresh the snapshot in case the env changed since mount.
      loadSnapshot();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Serverfeil";
      setLastResult({ ok: false, message: `Kunne ikke sende test-e-post: ${detail}` });
      setVerified("fail");
      toast.error("Kunne ikke sende test-e-post");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4"
      data-testid="email-health-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`shrink-0 inline-flex w-9 h-9 rounded-xl items-center justify-center ${
              pillOk
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
            }`}
          >
            {pillOk ? (
              <CheckCircle2 className="w-5 h-5" strokeWidth={1.75} />
            ) : (
              <AlertTriangle className="w-5 h-5" strokeWidth={1.75} />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-semibold flex items-center gap-2 flex-wrap">
              <Mail className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
              <span data-testid="email-health-status">
                {pillOk ? "E-postoppsett: OK" : "E-postoppsett: Feil"}
              </span>
              {verified === "ok" && (
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                  data-testid="email-health-verified-tag"
                >
                  Verifisert
                </span>
              )}
              {verified === "fail" && (
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  data-testid="email-health-verified-tag"
                >
                  Test feilet
                </span>
              )}
              {verified === null && (
                <span
                  className="text-[10px] uppercase tracking-wider font-medium rounded-full px-2 py-0.5 bg-muted text-muted-foreground"
                  data-testid="email-health-verified-tag"
                >
                  Ikke testet
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 break-all">
              Provider: <strong>{snapshot?.provider}</strong>
              {snapshot?.email_from ? ` · Fra: ${snapshot.email_from}` : ""}
            </p>
            {snapshot?.public_base_url && (
              <p
                className="text-[11px] text-muted-foreground/80 mt-0.5 break-all"
                data-testid="email-health-base-url"
              >
                Avbestillingslenker: {snapshot.public_base_url}/avbestill/…
              </p>
            )}
          </div>
        </div>
      </div>

      <form
        onSubmit={submitTest}
        className="flex flex-col sm:flex-row gap-2 sm:items-end"
      >
        <div className="flex-1 min-w-0">
          <Label htmlFor="email-health-to" className="text-xs">
            Send test-e-post til
          </Label>
          <Input
            id="email-health-to"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="navn@bedrift.no"
            className="rounded-xl mt-1.5"
            autoComplete="email"
            data-testid="email-health-to-input"
          />
        </div>
        <Button
          type="submit"
          className="rounded-xl"
          disabled={sending}
          data-testid="email-health-send-btn"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Sender...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Send test-e-post
            </>
          )}
        </Button>
      </form>

      {lastResult && (
        <div
          className={`rounded-xl border px-3 py-2 text-sm ${
            lastResult.ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200"
              : "border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200"
          }`}
          data-testid="email-health-result"
        >
          {lastResult.message}
        </div>
      )}
    </section>
  );
}
